import { Taggable } from '../core/core';
import { PersonMatcher } from '../person/person-matcher';
import { ChannelMessageResponse } from './channel-message';
import { ChannelNotifications } from './channel-notifications';


export enum ChannelTyperStatus {
    Typing = 'typing',
    Talking = 'talking',
    None = 'none',
}

export enum ChannelType {
    /**
     * Admin Defined Channel (ADC)
     */
    Channel = 'channel',
    /**
     * User Defined Channel (UDC)
     */
    People = 'people',
    /**
     * Channel created to report a safety observation
     */
    SafetyObservation = 'safety-observation',
    /**
     * Channel created to facilitate support conversations with a
     * weavix support agent
     */
    SupportTicket = 'support-ticket',
    /**
     * Channel created when an API calls the admin api endpoint
     * POST: /api/admin/channel-message
     */
    AdminApi = 'admin-api',
    /**
     * Unique per-user channel for notes.
     *
     * WARNING: currently nothing is creating this type of channel; it only exists to introduce backward compatibility.
     * TODO(post-5.19): once all clients have updated to 5.19, start creating new notebooks with this type and backfill existing notebooks.
     */
    Notebook = 'notebook',
    /**
     * Local-only channel, never synced to the server.
     */
    Local = 'local',
    /**
     * Channel created for AI voice assistant interactions.
     */
    AiVoiceAssistant = 'ai-voice-assistant',
}

/**
 * These channel types are stored at and isolated to the account level
 */
export const AccountChannelTypes = [ChannelType.Channel, ChannelType.SafetyObservation, ChannelType.AdminApi];

/**
 *  These channel types are stored in the master database and are cross-account accessible
 */
export const GlobalChannelTypes = [ChannelType.People, ChannelType.SupportTicket, ChannelType.Notebook, ChannelType.AiVoiceAssistant];

export type ArchivedMap = { [personId: string]: Date | string };

export type AccessInfo = {
    from?: Date;
    to?: Date;
};
export type AccessMap = { [personId: string]: AccessInfo };

export type ChannelEncryptionKey = {
    created: Date;
    inactive?: Date;
    key: string;
    /**
     * The random 8-byte seed (hex) the `key` was derived from via SHA-256. Newer
     * keys are seed-based; the seed (not the full key) is distributed over the mesh
     * so peers rebuild the key locally. Absent on keys created before seed-based
     * derivation — those fall back to identity derivation over the mesh.
     */
    seed?: string;
};

export interface Channel extends Omit<Taggable, 'facilityId'>, PersonMatcher {
    id?: string;
    accountId?: string;
    facilityIds?: string[];
    created?: Date | string;
    /** used for checking connection when getting channels to see if they can subscribe or just show the accept/decline prompt */
    creatorId?: string;
    /** User-defined channel membership; ADC membership is defined by conditions. */
    people?: string[];
    conditionalNotificationOverrides?: ChannelConditionalOverride[];
    defaultNotification?: ChannelNotifications;
    defaultFavorite?: ChannelDefaultFavorite;
    isReadOnly?: boolean;
    name?: string;
    allPeople?: string[];
    fullAccess?: boolean;
    preferencesDisabled?: boolean;
    readOnlySenders?: string[];

    /**
     * Access dates for individual people who join late or are removed from the channel.
     */
    access?: AccessMap;

    /**
      * A number that increases every time a message is sent on the channel.
      * This is effectively the total number of messages on the channel. The
      * number may be lower than the actual number of messages on the channel
      * if the channel existed before we implemented this field.
      *
      * Previously we used the totalMessageCount field for this, but we made a new
      * field so that we could have backwards compatibility while we transitioned
      * to a new way of calculating badge counts.
    */
    sequence?: number;

    /**
      * A number representing how many messages a user has read on the channel.
      * This cannot be larger than the sequence field above.
      *
      * The difference between sequence and readSequence is the number of unread messages.
      *
      * This is present on UDCs only, and is used to show which messages have been read
      * by other users. To see how many messages the current user has read, see the
      * `readSequence` (singular) field on the ChannelResponse interface below.
      *
      * ADCs can have a ton of users so we do not support this feature on ADCs.
      *
      * Previously we used the messageCounts field for this, but we made a new
      * field so that we could have backwards compatibility while we transitioned
      * to a new way of calculating badge counts.
    */
    readSequences?: { [userId: string]: number };
    /**
     * Transcribe and translate PTT audio phrase by phrase -- chunking -- while the speaker is still
     * talking, rather than once the transmission ends.
     *
     * Admin-defined channels ({@link ChannelType.Channel}) only — every other channel type uses the
     * whole-file path, and the API rejects the field on them. Off by default: it trades accuracy for
     * text arriving sooner, and only some customers want that trade.
     */
    liveTranscription?: boolean;
    type?: ChannelType;
    typers?: ChannelTyper[];
    roips?: Array<{ id: string, radio: string }>;
    updated?: Date | string;
    radioChannel?: number;
    radioSquelch?: number;
    lastAutoReplies?: { [userId: string]: AutoReplyEvent };
    teamsChatId?: string;
    task?: ChannelTask;

    /**
     * channelImageUrl only applies to channelType === ChannelType.AdminApi
     * A URL to an image that represents the channel.
     */
    channelImageUrl?: ChannelImageUrl;
    /**
     * responseUrl only applies to channelType === ChannelType.AdminApi
     * A URL that weavix will POST to when a user responds in the channel.
     */
    responseUrl?: string;

    /**
     * When data retention policies are applied, any messages older than this date
     * have been deleted. On UDC (people) channels, this is a map of userIds to dates
     * because users may be on different home companies and therefore have different
     * data retention policies. On ADC (channel) channels, this is a single date for
     * the whole channel since only account members exist on the channel.
     */
    archived?: Date | string | ArchivedMap;

    /**
     * Only applicable in ai channels
     * Last session ended date
     */
    lastSessionEnded?: Date | string;

    /**
     * A list of encryption keys for the channel.
     */
    encryptionKeys?: ChannelEncryptionKey[];

    /**
     * A unique identifier for this channel on the mesh.
     */
    meshId?: number;

    /**
     * The id a device addressed this channel by before the server assigned {@link meshId}: its
     * creation time masked (`created & 0x1fffffff`). Lets traffic sent before the server's id synced
     * down still resolve here. Only set for channels created from the mesh.
     */
    tempMeshId?: number;

    /**
     * A color hex code string representing the channel color.
     * Used for Speaker Mic's.
     */
    color?: string;

    /**
     * Only applicable in ai channels
     * Keywords or phrases that describe the purpose of this channel
     */
    smartAssistantChannelDescription?: string;
    smartAssistantKeywords?: string[];
}

export const ENCRYPTION_KEY_ROTATE_MS = 7 * 24 * 3_600_000; // regularly rotate keys
export const ENCRYPTION_KEY_INACTIVE_TIME_MS = 12 * 3_600_000; // deactivated keys should stop being accepted after this time
export const ENCRYPTION_KEY_DELAY_TIME_MS = 6 * 3_600_000; // new keys should not be used until this time has passed
export const ENCRYPTION_KEY_MAX_COUNT = 2; // only need two keys

export interface ChannelImageUrl {
    externalUrl: string;
    internalUrl: string;
}

export enum TaskChannelType {
    SafetyObservation = 'safetyObservation',
    SupportTicket = 'supportTicket',
}

export type ChannelTask = SafetyObservationTask | SupportTicketTask;
export type SafetyObservationTaskState = 'initializing' | 'ai-processing' | 'awaiting-reporter-feedback' | 'done';
export interface SafetyObservationTask {
    id?: string;
    facilityId: string;
    location?: number[];
    geofenceId?: string;
    ticketNumber?: number;
    state: SafetyObservationTaskState;
}

export interface SupportTicketTask {
    ticketId?: string;
    status: 'pending' | 'open' | 'closed';
}

export interface ChannelTyper {
    personId: string;
    status: ChannelTyperStatus;
    expires: Date;
}

/**
 * The data published on the Topic.AccountChannelTypers mqtt topic.
 */
export interface ChannelTypers {
    typers: ChannelTyper[];
}

export interface AutoReplyEvent {
    sentAt: Date;
    messageHash: string;
}

export interface ChannelResponse extends Channel {
    notification?: ChannelNotifications;

    /**
     * This field is not stored on the channel record itself in the database.
     * Instead, it's stored on the channel preference for the user/channel.
     * This field is populated from the channel preference and represents the
     * readSequence value for the logged-in user.
     *
     * We use this field to calculate the unread badge count for a channel for
     * the current user. This is present on both UDCs and ADCs, unlike the
     * `readSequences` (plural) field on the Channel interface above.
     */
    readSequence?: number;

    lastMessages?: ChannelMessageResponse[];
    favoriteIndex?: number;
    isSnoozed?: boolean;
}

export interface ChannelTyperRequest {
    status: ChannelTyperStatus;
    expires?: Date;
}

/** UDC */
export interface CreateGroupRequest {
    id?: string;
    name?: string;
    defaultNotification?: ChannelNotifications;
    people?: string[];
    tags?: string[];
    new?: boolean;
    teamsChatId?: string;
    task?: ChannelTask;
    type?: ChannelType;
    created?: Date | string;
    /**
     * Seed (hex) for the channel's first encryption key, supplied when a device
     * created the channel offline so the backend derives the identical key.
     */
    seed?: string;
}

/** UDC */
export interface UpdateGroupRequest extends PersonMatcher {
    name?: string;
    fullAccess?: boolean;
    remove?: string;
    add?: string;
    people?: string[];
    defaultNotification?: ChannelNotifications;
    tags?: string[];
    access?: { [personId: string]: { from?: Date, to?: Date } };
}

/** ADC */
export interface CreateChannelRequest extends PersonMatcher {
    name?: string;
    fullAccess?: boolean;
    conditionalNotificationOverrides?: ChannelConditionalOverride[];
    defaultNotification?: ChannelNotifications;
    defaultFavorite?: ChannelDefaultFavorite | null;
    preferencesDisabled?: boolean;
    tags?: string[];
    facilityIds?: string[];
    roips?: Array<{ id: string, radio: string }>;
    isReadOnly?: boolean;
    readOnlySenders?: string[];
    radioChannel?: number;
    folderId?: string;
    /** See {@link Channel.liveTranscription}. Admin-defined channels only. */
    liveTranscription?: boolean;
}

export interface ChannelReadResponse {
    channelId?: string;
    readSequence?: number;
}

/** Indicates the ADC is a default favorite. `null` if not. */
export interface ChannelDefaultFavorite {
    addedOn: Date;
}

/**
 * A conditional override for an ADC.
 *
 * Was previously called `ConditionalChannelNotificationType`.
 */
export interface ChannelConditionalOverride extends ChannelNotifications, PersonMatcher {
    description: string;
    defaultFavorite?: ChannelDefaultFavorite | null;
}
